import { useState, useEffect } from "react";
import { RiMenu3Line, RiCloseLine } from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";

interface NavLinkProps {
  href: string;
  text: string;
  active?: boolean;
}

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    // console.log("Toggling menu, isOpen was:", isOpen);
    setIsOpen(!isOpen);
  };

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // console.log(
    //   "Overlay clicked, target:",
    //   e.target,
    //   "currentTarget:",
    //   e.currentTarget,
    // );
    // Only close if clicking on the overlay itself, not its children
    if (e.target === e.currentTarget) {
      console.log("Closing menu due to outside click");
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        // Trigger background change after 50px of scroll
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const NavLink: React.FC<NavLinkProps> = ({ href, text, active = false }) => {
    return (
      <Link
        to={href}
        className={`whitespace-nowrap p-2 uppercase tracking-widest transition-colors duration-200 hover:text-[#1a4fa3] xl:text-xl 2xl:px-4 ${
          active ? "font-semibold" : ""
        } ${isScrolled ? "text-black" : "text-black"} `}
        style={{ fontFamily: "RingsideMedium" }}
      >
        {text}
      </Link>
    );
  };

  // Determine logo based on route
  const logoSrc =
    location.pathname === "/nakshatra"
      ? "/assets/imgs/NakshatraLogo.png"
      : "/assets/imgs/CenturyLogo.png";

  const isNakshatra = location.pathname === "/nakshatra";

  return (
    <nav
      className={`left-0 top-0 z-[100] w-full bg-[#f0f0f0] transition-all duration-500 ease-out ${
        isScrolled
          ? // ? "bg-opacity-80 bg-gradient-to-r from-gray-800 to-black shadow-lg" // Gradient background on scroll
            // "bg-white bg-opacity-80 shadow-lg" // Gradient background on scroll
            // "scale-100 bg-[#fffbf0] shadow-lg" // Gradient background on scroll
            "fixed scale-100 bg-[#f0f0f0] shadow-lg" // Gradient background on scroll
          : // : "header-gradient bg-transparent" // Transparent initially
            // "scale-105 bg-transparent" // Transparent initially
            "scale-100 bg-transparent" // Transparent initially
      } ${isOpen ? "h-screen pt-4" : ""}`}
    >
      {/* Desktop Navigation */}
      <div className="mx-auto my-2 w-full px-4 xl:max-w-[85vw] xl:px-0 [@media(min-width:2000px)]:max-w-[1500px]">
        <div className="mx-4 flex h-16 items-center justify-between lg:mx-0">
          {/* Logo and Left Nav Links */}
          <div className="flex items-center">
            <div className="flex-shrink-0 lg:hidden">
              <Link to={"/"}>
                <img
                  src={logoSrc} // Use dynamic logo for mobile
                  alt="Century Group"
                  className="h-10 w-auto"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src =
                      "https://placeholder.pics/svg/180x40/FFFFFF/000000/CENTURY%20GROUP";
                    target.onerror = null;
                  }}
                />
              </Link>
            </div>

            <div className="hidden lg:block">
              <div className="flex items-baseline">
                <NavLink href="/" text="HOME" />
                <NavLink href="/about" text="ABOUT US" />
                {/* <NavLink href="/events" text="Activity & Events" /> */}
                <NavLink href="/projects" text="PROJECTS" />
              </div>
            </div>
          </div>

          {/* Center Logo for Desktop */}
          <div className="hidden lg:flex">
            <Link to="/">
              <img
                src={logoSrc} // Use dynamic logo for desktop
                alt="Century Group Logo"
                className={`h-auto ${isNakshatra ? "w-20 xl:w-24" : "w-40 xl:w-48"} `}
              />
            </Link>
          </div>

          {/* Right Nav Links and Icons */}
          <div className="hidden lg:block">
            <div className="flex items-baseline">
              <NavLink href="/gallery" text="GALLERY" />
              <NavLink href="/contact-us" text="CONTACT" />
              <NavLink href="/enquire" text="ENQUIRE NOW" />
            </div>
            {/* <NavLink href="/enquireNow" text="Enquire Now" /> */}
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              onClick={toggleMenu}
              className="p-2 text-[#D4AF37] hover:text-[#1a4fa3] focus:outline-none"
            >
              {isOpen ? (
                <RiCloseLine className="h-6 w-6" />
              ) : (
                <RiMenu3Line className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - Right side sliding */}
      <div
        className={`fixed right-0 top-0 z-50 h-screen transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } w-full sm:w-[350px]`}
      >
        <div className="h-screen bg-black">
          <div className="flex h-screen flex-col">
            {/* Close Button */}
            <div className="mr-4 mt-4 flex justify-end p-4">
              <button
                onClick={toggleMenu}
                className="text-white hover:text-[#1a4fa3] focus:outline-none"
              >
                <RiCloseLine className="h-8 w-8" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex flex-1 flex-col space-y-4 px-6 py-4">
              {[
                "Home",
                "About Us",
                "Projects",
                // "Activity & Events",
                "Gallery",
                "Contact Us",
                "Enquire Now",
              ].map((item) => (
                <Link
                  key={item}
                  to={
                    item === "Home"
                      ? "/"
                      : item === "Activity & Events"
                        ? "/events" // Directly link to the events route
                        : item === "Enquire Now"
                          ? "/contact-us#contact-form" // Link to the contact form section
                          : `/${item.toLowerCase().replace(/ /g, "-")}`
                  }
                  className="duration-20 group flex items-center justify-between whitespace-nowrap text-base font-semibold uppercase text-white transition-colors hover:text-[#1a4fa3] group-hover:text-[#1a4fa3]"
                  onClick={toggleMenu}
                >
                  {item}
                  <span className="text-white group-hover:text-[#1a4fa3]">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay with click handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={handleOutsideClick}
        ></div>
      )}
    </nav>
  );
};

export default Navbar;
